import { ReactElement } from "react";
import {
	IntegrationFlowValidationResultAppSampleModel,
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { DateString, FormItem } from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { IInputAttachable, IObjectInputAttachable } from "client/Forms/attachable";
import { isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { AppMappingStepConfigurationFormData, ResultMapperStyledFieldFormData } from "./common";
import { ResultMapperFormResult } from "./ResultMapperFormResult";
import { ExpressionTextInput } from "./ExpressionTextInput";
import { AppResultFieldWithColorActions } from "./AppResultFieldWithColorAction";
import { ColorSelector } from "./ColorSelector";

export type AppFormProps = {
	attachable: IObjectInputAttachable<AppMappingStepConfigurationFormData>;
	appSample: OptionalModel<IntegrationFlowValidationResultAppSampleModel> | null;
	isReadOnly: boolean;
	timeZone: TimeZone;
};

export const AppForm = (props: AppFormProps): ReactElement => {
	const {
		attachable,
		appSample,
		isReadOnly,
		timeZone
	} = props;

	const linkExpressionHandler: IInputAttachable<string> = attachable.for("linkExpression");
	const titleHandler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("title");
	const dateHandler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("date");
	const subtitle1Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("subtitle1");
	const subtitle2Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("subtitle2");
	const subtitle3Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("subtitle3");
	const descriptionHandler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("description");
	const hasResult: boolean = isNotNil(appSample) && appSample.isSet;

	const appSampleValue: IntegrationFlowValidationResultAppSampleModel | null = isNotNil(appSample)
		&& appSample.isSet
			? appSample.value
			: null;

	return (
		<>
			<FormItem
				label={localize("_Title")}
				isRequired
			>
				<AppResultFieldWithColorActions
					attachable={titleHandler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={appSampleValue?.title}
				/>
			</FormItem>
			{titleHandler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									appSampleValue?.title
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={localize("_Link")}
				isRequired
			>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={linkExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={linkExpressionHandler}
					hasResult={hasResult}
					resultValue={appSampleValue?.link}
				/>
			</FormItem>
			<FormItem
				label={localize("_DateCreatedModified")}
			>
				<AppResultFieldWithColorActions
					attachable={dateHandler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={appSampleValue?.dateTime}
				>
					{appSampleValue?.dateTime?.value && (
						<DateString
							value={appSampleValue?.dateTime.value}
							timezone={timeZone}
							format="RelativeDayWithTime"
						/>
					)}
				</AppResultFieldWithColorActions>
			</FormItem>
			{dateHandler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									appSampleValue?.dateTime
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "1"
						}
					]
				}}
			>
				<AppResultFieldWithColorActions
					attachable={subtitle1Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={appSampleValue?.subTitle1}
				/>
			</FormItem>
			{subtitle1Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									appSampleValue?.subTitle1
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "2"
						}
					]
				}}
			>
				<AppResultFieldWithColorActions
					attachable={subtitle2Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={appSampleValue?.subTitle2}
				/>
			</FormItem>
			{subtitle2Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									appSampleValue?.subTitle2
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "3"
						}
					]
				}}
			>
				<AppResultFieldWithColorActions
					attachable={subtitle3Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={appSampleValue?.subTitle3}
				/>
			</FormItem>
			{subtitle3Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									appSampleValue?.subTitle3
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={localize("_Description")}
			>
				<AppResultFieldWithColorActions
					attachable={descriptionHandler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={appSampleValue?.description}
				/>
			</FormItem>
			{descriptionHandler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									appSampleValue?.description
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
		</>
	);
};
AppForm.displayName = "AppForm";